import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import {
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Paper,
  Checkbox,
  IconButton,
  Menu,
  MenuItem,
  Table as MuiTable,
  // Switch,
  styled,
  Stack,
  Tooltip,
} from '@mui/material';
import { isEmpty } from 'lodash-es';
import React, { useEffect, useState } from 'react';
import {
  useGlobalFilter,
  useTable,
  Row,
  TableOptions,
  UseTableColumnProps,
  Cell,
} from 'react-table';

import WsColors from '../../WsColors';
import { Item } from '../Item';
import { WsPagination } from '../WsPagination';

const CustomTableHeadRow = styled(TableRow)(() => ({
  background: WsColors.White,
  padding: 8,
}));

const CustomTableHeadCell = styled(TableCell)(({ pointer }: { pointer: boolean }) => ({
  paddingLeft: 32,
  fontWeight: 700,
  fontSize: '14px',
  color: WsColors.TertiaryShades900,
  cursor: pointer ? 'pointer' : 'default',
  '&:focus, &:focus-within': {
    outline: 'none',
  },
  '&:hover': {
    backgroundColor: 'transparent !important',
  },
}));

const CustomTableBodyRow = styled(TableRow)(() => ({
  background: WsColors.White,
  '&:hover': {
    backgroundColor: 'transparent !important',
  },
  '&:focus, &:focus-within': {
    outline: 'none',
  },
}));

interface ICustomMenuProps {
  rowId: number;
  rowActions: RowActionType[];
  row: Row;
}

const CustomMenu = ({ rowId, rowActions, row }: ICustomMenuProps) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(anchorEl);
  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) =>
    setAnchorEl(event.currentTarget);

  const handleMenuClose = () => setAnchorEl(null);

  const handleMenuOptionSelect = (option: RowActionType) => {
    option.callback(row.cells, rowId);
    handleMenuClose();
  };
  return (
    <>
      <IconButton
        aria-label="more"
        id="long-button"
        aria-controls={menuOpen ? 'long-menu' : undefined}
        aria-expanded={menuOpen ? 'true' : undefined}
        aria-haspopup="true"
        onClick={handleMenuClick}
      >
        <MoreVertIcon />
      </IconButton>
      <Menu
        id="long-menu"
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={handleMenuClose}
        elevation={1}
      >
        {rowActions?.map((option: RowActionType, i: number) => (
          <MenuItem key={i} selected={i === 0} onClick={() => handleMenuOptionSelect(option)}>
            {option.label}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

type CustomTableBodyCellPropsType = {
  cell: Cell;
  boldItems?: string[];
  dense?: boolean;
  pointer: boolean;
};

const CustomTableBodyCell = styled(TableCell)(
  ({ cell, boldItems, dense, pointer }: CustomTableBodyCellPropsType) => ({
    paddingLeft: 32,
    paddingTop: dense ? 16 : 24,
    paddingBottom: dense ? 16 : 24,
    fontWeight: boldItems?.includes(cell.column.id) ? 700 : 400,
    fontSize: boldItems?.includes(cell.column.id) ? '16px' : '14px',
    cursor: pointer ? 'pointer' : 'default',
  })
);

type RowActionType = {
  label: string;
  callback: (arg1: Cell[], arg2: number) => void;
};

type CellCallbackType = {
  id: string;
  callback: (arg1: string, arg2: number) => void;
};

export interface WsDataTablePropsType extends TableOptions<Record<string, unknown>> {
  totalDataItems?: number;
  fetchPage?: (arg: number) => void;
  selectable?: boolean;
  preSelectedRows?: number[];
  rowActions?: Array<RowActionType>;
  boldItems?: string[];
  dense?: boolean;
  headless?: boolean;
  pageable?: boolean;
  elevated?: boolean;
  getSelectedRows?: (arg: number[]) => void;
  handleSwitchChange?: (arg1: number, arg2: boolean) => void;
  sortableColumn?: string;
  handleSort?: (arg: 'ASC' | 'DESC' | 'NONE') => void;
  cellCallbacks?: Array<CellCallbackType>;
  page?: number;
  pageSize?: number;
  onPageChange?: (page: number) => void;
  onRowClick?: (row: Row, e: React.MouseEvent<HTMLElement>) => void;
  toolTipTextOnRowHover?: string;
}

const WsDataTable: React.FC<WsDataTablePropsType> = ({
  columns,
  data,
  totalDataItems,
  fetchPage,
  selectable = false,
  preSelectedRows = [],
  rowActions,
  boldItems,
  dense = false,
  headless = false,
  pageable = true,
  elevated = true,
  getSelectedRows = () => {},
  // handleSwitchChange,
  sortableColumn,
  handleSort = () => {},
  cellCallbacks = [],
  page,
  pageSize,
  onPageChange,
  onRowClick,
  toolTipTextOnRowHover = '',
}) => {
  const { getTableProps, headerGroups, rows, prepareRow } = useTable(
    {
      columns,
      data,
    },
    useGlobalFilter
  );

  const [selectedRows, setselectedRows] = useState<number[]>(preSelectedRows);

  const addSelectedRow = (rowId: number) => setselectedRows((prev) => [...prev, rowId]);

  const removeSelectedRow = (rowId: number) =>
    setselectedRows((prev) => prev.filter((index) => index != rowId));

  const selectAll = () => setselectedRows(Array.from(Array(rows.length).keys()));

  const deselectAll = () => setselectedRows([]);

  useEffect(() => getSelectedRows(selectedRows), [getSelectedRows, selectedRows]);

  return (
    <TableContainer component={Paper} elevation={elevated ? 1 : 0}>
      <MuiTable {...getTableProps()}>
        <>
          {!headless && (
            <TableHead>
              {headerGroups.map((headerGroup, key1) => (
                <React.Fragment key={key1}>
                  <CustomTableHeadRow {...headerGroup.getHeaderGroupProps()}>
                    {selectable && (
                      <TableCell padding="checkbox" component="span">
                        <Checkbox
                          checked={selectedRows.length === rows.length}
                          onChange={(ev) => (ev.target.checked ? selectAll() : deselectAll())}
                        />
                      </TableCell>
                    )}
                    {headerGroup.headers.map(
                      (column: UseTableColumnProps<Record<string, unknown>>, key2) => (
                        <React.Fragment key={key2}>
                          <CustomTableHeadCell
                            variant="head"
                            pointer={!isEmpty(sortableColumn)}
                            {...column.getHeaderProps()}
                            onClick={() => handleSort('NONE')}
                          >
                            <Stack direction="row" alignItems={'center'}>
                              {column.render('Header')}
                              {column.id === sortableColumn && (
                                <>
                                  <KeyboardArrowDown
                                    sx={{ cursor: 'pointer' }}
                                    onClick={() => handleSort('ASC')}
                                  />
                                  <KeyboardArrowUp
                                    sx={{ cursor: 'pointer' }}
                                    onClick={() => handleSort('DESC')}
                                  />
                                </>
                              )}
                            </Stack>
                          </CustomTableHeadCell>
                        </React.Fragment>
                      )
                    )}
                    {rowActions && <TableCell padding="checkbox" component="span"></TableCell>}
                  </CustomTableHeadRow>
                </React.Fragment>
              ))}
            </TableHead>
          )}
          <TableBody>
            {rows.map((row: Row, rowId: number) => {
              prepareRow(row);
              return (
                <React.Fragment key={rowId}>
                  <CustomTableBodyRow
                    hover
                    {...row.getRowProps()}
                    {...(onRowClick && {
                      onClick: (e) => onRowClick(row, e),
                    })}
                  >
                    {selectable && (
                      <TableCell padding="checkbox" component="span">
                        <Checkbox
                          checked={selectedRows.includes(rowId)}
                          onChange={(ev) =>
                            ev.target.checked ? addSelectedRow(rowId) : removeSelectedRow(rowId)
                          }
                        />
                      </TableCell>
                    )}
                    {row.cells.map((cell, key) => {
                      // if (typeof cell.value === 'boolean') {
                      //   return (
                      //     <React.Fragment key={key}>
                      //       <TableCell sx={{ py: dense ? 2 : 3, px: 4 }} {...cell.getCellProps()}>
                      //         <Switch
                      //           onChange={(ev) =>
                      //             handleSwitchChange && handleSwitchChange(rowId, ev.target.checked)
                      //           }
                      //           defaultChecked={cell.value}
                      //         />
                      //       </TableCell>
                      //     </React.Fragment>
                      //   );
                      // }
                      return (
                        <React.Fragment key={key}>
                          <Tooltip title={toolTipTextOnRowHover} placement="top">
                            <CustomTableBodyCell
                              cell={cell}
                              pointer={!isEmpty(rowActions ?? onRowClick ?? cellCallbacks)}
                              boldItems={boldItems}
                              {...cell.getCellProps()}
                              dense={dense}
                              onClick={() =>
                                cellCallbacks.map((callbacks: CellCallbackType) =>
                                  callbacks.id === cell.column.id
                                    ? callbacks.callback(cell.value, rowId)
                                    : null
                                )
                              }
                              sx={{ cursor: onRowClick ? 'pointer' : 'text' }}
                            >
                              {cell.render('Cell')}
                            </CustomTableBodyCell>
                          </Tooltip>
                        </React.Fragment>
                      );
                    })}
                    {rowActions && (
                      <TableCell padding="checkbox" component="span">
                        <CustomMenu rowId={rowId} rowActions={rowActions} row={row} />
                      </TableCell>
                    )}
                  </CustomTableBodyRow>
                </React.Fragment>
              );
            })}
          </TableBody>
        </>
      </MuiTable>
      {pageable && totalDataItems && (
        <Item>
          <WsPagination
            count={Math.ceil(totalDataItems / Number(pageSize ?? 50))}
            itemsPerPage={pageSize ?? 50}
            defaultPage={page}
            onChange={(ev, newPage) => {
              fetchPage && fetchPage(newPage);
              setselectedRows([]);
              onPageChange && onPageChange(newPage);
            }}
          />
        </Item>
      )}
    </TableContainer>
  );
};

export default WsDataTable;
